import type { ChecklistItem } from '../../../types/timeline'
import type { TimelineElement } from '../../../types/timelineElement'
import type { WindowMs } from './completionSeries'
import type { ScopedChild } from './scopeChildren'

export const CADENCE_BUCKETS = [
  { label: '1d', min: 0, maxInclusive: 1 },
  { label: '2d', min: 1, maxInclusive: 2 },
  { label: '3d', min: 2, maxInclusive: 3 },
  { label: '4–7d', min: 3, maxInclusive: 7 },
  { label: '8–14d', min: 7, maxInclusive: 14 },
  { label: '15+d', min: 14, maxInclusive: Infinity },
] as const

function toTimestampMs(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }
  if (typeof value === 'string') {
    const ms = Date.parse(value)
    return Number.isFinite(ms) ? ms : undefined
  }
  if (typeof value === 'object') {
    if ('toMillis' in value && typeof (value as { toMillis: unknown }).toMillis === 'function') {
      const ms = (value as { toMillis: () => unknown }).toMillis()
      return typeof ms === 'number' && Number.isFinite(ms) ? ms : undefined
    }
    if ('seconds' in value && typeof (value as { seconds: unknown }).seconds === 'number') {
      const obj = value as { seconds: number; nanoseconds?: unknown }
      const sec = obj.seconds
      const nano = typeof obj.nanoseconds === 'number' ? obj.nanoseconds : 0
      const ms = sec * 1000 + Math.floor(nano / 1_000_000)
      return Number.isFinite(ms) ? ms : undefined
    }
  }
  return undefined
}

export function toCompletionTimes(element: unknown): { createdAtMs?: number; completedAtMs?: number } {
  if (!element || typeof element !== 'object') {
    return { createdAtMs: undefined, completedAtMs: undefined }
  }
  const el = element as { createdAt?: unknown; completedAt?: unknown; createdAtMs?: unknown; completedAtMs?: unknown }
  const createdAtMs = toTimestampMs(el.createdAt) ?? (typeof el.createdAtMs === 'number' && Number.isFinite(el.createdAtMs) ? el.createdAtMs : undefined)
  const completedAtMs = toTimestampMs(el.completedAt) ?? (typeof el.completedAtMs === 'number' && Number.isFinite(el.completedAtMs) ? el.completedAtMs : undefined)
  return { createdAtMs, completedAtMs }
}

type TimestampCarrier = { createdAtMs?: unknown; completedAtMs?: unknown }
type Candidate = TimelineElement | ChecklistItem | TimestampCarrier
const dayMs = 86_400_000

const descendants = (parent: TimelineElement, elementsById: ReadonlyMap<string, TimelineElement>): TimelineElement[] =>
  [...elementsById.values()].filter((candidate) => {
    let parentId = candidate.parentId
    while (parentId) {
      if (parentId === parent.id) return true
      parentId = elementsById.get(parentId)?.parentId ?? null
    }
    return false
  })

const bucketFor = (days: number): number => CADENCE_BUCKETS.findIndex((bucket, index) =>
  (index === 0 ? days >= 0 : days > bucket.min) && days <= bucket.maxInclusive,
)

export function completionBuckets(
  parents: readonly ScopedChild[],
  elementsById: ReadonlyMap<string, TimelineElement>,
  _window: WindowMs,
): number[][] {
  void _window
  return parents.map((parent) => {
    const counts = Array.from({ length: CADENCE_BUCKETS.length }, () => 0)
    const candidates: Candidate[] = parent.element
      ? [parent.element, ...descendants(parent.element, elementsById)]
      : parent.checklistItem ? [parent.checklistItem] : []
    candidates.forEach((candidate) => {
      const createdAtMs = 'createdAtMs' in candidate ? candidate.createdAtMs : undefined
      const completedAtMs = 'completedAtMs' in candidate ? candidate.completedAtMs : undefined
      if (typeof createdAtMs !== 'number' || typeof completedAtMs !== 'number') return
      const bucket = bucketFor((completedAtMs - createdAtMs) / dayMs)
      if (bucket >= 0) counts[bucket] += 1
    })
    return counts
  })
}
